/**
 * RBAC-4.1 · FR-30. Choose a role for a person and see what it means before confirming: the target
 * role's holder count and, by area, what it lets them do (AC 1). What it replaces is said in the
 * same breath (FR-34), and a role the database would refuse under RBAC-1.5 is named before the admin
 * tries, with the confirm disabled (AC 4). The refusal itself is the database's.
 *
 * Copy lives in `lib/personRole.ts` (tested); this file only lays it out.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AccountRole } from '@/lib/accountRoles';
import {
  assignPersonRoleErrorMessage,
  changeTargets,
  isStaleRole,
  roleChangePreview,
  roleSystemStatement,
  type PersonRoleAssignment,
} from '@/lib/personRole';
import { fetchAccountRoles } from '@/services/accountRolesService';
import { PersonRoleError, assignPersonRole, fetchReservedNotHeld } from '@/services/personRoleService';

export interface ChangeRoleDialogProps {
  open: boolean;
  assignment: PersonRoleAssignment;
  onOpenChange: (open: boolean) => void;
  /** After a change, or after a 22023 saying the role moved underneath: re-read the record. */
  onChanged: (roleId: number | null) => void;
}

export function ChangeRoleDialog({ open, assignment, onOpenChange, onChanged }: ChangeRoleDialogProps) {
  const [roles, setRoles] = useState<AccountRole[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string>('');
  const [reserved, setReserved] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetId('');
    setReserved(null);
    setError(null);
    setStale(false);
    setLoadError(null);
    let cancelled = false;
    fetchAccountRoles()
      .then((r) => !cancelled && setRoles(r))
      .catch((e: Error) => !cancelled && setLoadError(e.message || 'The roles could not be read.'));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const targets = useMemo(() => (roles ? changeTargets(roles, assignment) : []), [roles, assignment]);
  const target = targets.find((r) => String(r.id) === targetId) ?? null;

  useEffect(() => {
    setReserved(null);
    if (!target) return;
    let cancelled = false;
    fetchReservedNotHeld(target.id)
      .then((m) => !cancelled && setReserved(m))
      // Unknown is not "allowed": the database still decides, so fall back to no warning and let it.
      .catch(() => !cancelled && setReserved([]));
    return () => {
      cancelled = true;
    };
  }, [target]);

  const preview = target && reserved ? roleChangePreview({ assignment, target, reservedMissing: reserved }) : null;

  const confirm = async () => {
    if (!target || !preview || preview.blocked) return;
    setSaving(true);
    setError(null);
    try {
      await assignPersonRole(assignment.userId, target.id, assignment);
      onChanged(target.id);
      onOpenChange(false);
    } catch (e) {
      const rpc = e instanceof PersonRoleError ? e.rpc : { message: e instanceof Error ? e.message : undefined };
      setStale(isStaleRole(rpc));
      setError(assignPersonRoleErrorMessage(rpc));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" data-testid="change-role-dialog">
        <DialogHeader>
          <DialogTitle>{preview ? preview.title : 'Change role'}</DialogTitle>
          <DialogDescription>{roleSystemStatement(assignment)}</DialogDescription>
        </DialogHeader>

        {loadError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {!roles && !loadError && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {roles && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="change-role-target" className="text-sm font-medium">
                New role
              </label>
              {/* Native, not SearchableSelect: that one portals its list at z-300, under this
                  dialog's z-350, so it opens invisibly. */}
              <select
                id="change-role-target"
                value={targetId}
                onChange={(e) => {
                  setTargetId(e.target.value);
                  setError(null);
                  setStale(false);
                }}
                disabled={saving}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Choose a role</option>
                {targets.map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {r.isGlobal ? r.name : `${r.name} (your account's own)`}
                  </option>
                ))}
              </select>
            </div>

            {target && !preview && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}

            {preview && (
              <div className="space-y-3 rounded-md border border-border p-3" data-testid="change-role-preview">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{target?.name}</span>
                  <Badge variant="secondary">{preview.kind}</Badge>
                </div>
                <p className="text-sm" data-testid="change-role-holders">
                  {preview.holders}
                </p>
                {preview.emptySummary ? (
                  <p className="text-sm text-muted-foreground">{preview.emptySummary}</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What this role lets them do</p>
                    <ul className="space-y-1.5" data-testid="change-role-areas">
                      {preview.areas.map((a) => (
                        <li key={a.area} className="text-sm">
                          <span className="font-medium">{a.area}</span>
                          <span className="text-muted-foreground"> · {a.held.length} of {a.total}: </span>
                          <span>{a.held.join(', ')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-sm font-medium" data-testid="change-role-replaces">
                  {preview.replaces}
                </p>
              </div>
            )}

            {preview?.blocked && (
              <div
                role="alert"
                className="flex gap-2.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>{preview.blocked}</p>
              </div>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {stale ? (
            <Button
              onClick={() => {
                onChanged(null);
                onOpenChange(false);
              }}
            >
              Reload their record
            </Button>
          ) : (
            <Button onClick={() => void confirm()} disabled={!preview || !!preview.blocked || saving}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {preview ? preview.confirmLabel : 'Change role'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
